import { inject, Injectable, Injector } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpErrorResponse, HttpEvent } from '@angular/common/http';
import { LocalStorageService } from '../services/common/local-storage.service';
import { BehaviorSubject, catchError, filter, Observable, retry, switchMap, take, throwError, timer } from 'rxjs';

import { Router } from '@angular/router';
// Imported by relative path rather than through the `@sgm-frontend/core` barrel:
// a lib must not import its own barrel (it creates a cycle back through
// `interceptors/index.ts`, which breaks isolated unit tests of this file).
import { LanguageCode } from '../models/common/api-response.model';
import { CustomerAccessInput } from '../models/common/app-token.model';
import { RefreshAdminService } from '../services/api/admin/refreshAdmin.service';
import { ToastService } from '../services/common/toast.service';

/** Message shown when the session really is gone and the user must sign in again. */
export const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please log in again.';

/** Fallback message for an authorization (not authentication) failure. */
export const PERMISSION_DENIED_MESSAGE = 'You do not have permission to perform this action.';

/**
 * The backend uses HTTP 403 for two very different things and does not send a
 * machine-readable discriminator (`error_code` is `null` on every auth failure —
 * see `utils/jsend.js`), so the response message is the only signal available:
 *
 *  - `middleware/auth.js:99`  -> insufficient role. The session is perfectly valid.
 *  - `middleware/auth.js:112` -> access token predates a password change. Session is dead.
 *  - `controllers/auth-controller.js:331/347` -> refresh token rejected. Session is dead.
 *
 * Only messages matching one of these markers may tear the session down. Anything
 * else that arrives as a 403 is treated as an authorization failure and leaves the
 * session untouched — a spurious logout is far more damaging than a missed expiry,
 * and a genuine expiry surfaces as a 401 on the very next request anyway.
 */
const SESSION_INVALID_403_MARKERS = [
  'password change',
  'please login again',
  'invalid or expired refresh token',
  'invalid refresh token'
];

/** Auth endpoints must never be fed back into the refresh flow, or it deadlocks. */
const AUTH_ENDPOINTS = ['auth/refresh', 'auth/login', 'auth/forgot-password/update'];

@Injectable({ providedIn: 'root' })
export class AuthAdminInterceptor implements HttpInterceptor {
  private messageService = inject(ToastService);
  constructor(
    private injector: Injector,
    private readonly router: Router
  ) {

  }
  localStorageService = inject(LocalStorageService);
  // activeUserService = inject(ActiveUserService);
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);


  intercept(req: HttpRequest<any>, next: HttpHandler) {
    const deviceToken = this.localStorageService.getDiviceToken();
    // const deviceToken = localStorage.getItem('deviceToken');
    const token = this.localStorageService.getAdminToken();
    const lang = LanguageCode.English;
    // if (localStorage.getItem('app_language')) {
    //   if (localStorage.getItem('app_language') == 'ar') {
    //     lang = LanguageCode.Arabic;
    //   }
    // }
    const authReq = req;
    let authReqH = req;
    if (token) {
      authReqH = req.clone({
        headers: req.headers
          .set('Authorization', `Bearer ${token}`)
          .set('Device-Token', deviceToken)
          .set('Accept-Language', lang)
      });

    }
    else {
      authReqH = req.clone({
        headers: req.headers.set('Device-Token', deviceToken)
          .set('Accept-Language', lang)
      });

    }
    // this.activeUserService.updateGuestStatus();
    // Skip adding Authorization header for the auth endpoints themselves.
    if (this.isAuthEndpoint(req.url)) {
      // Deliberately no 401/403 handling here. Re-entering handle401Error for
      // `auth/refresh` used to leave `isRefreshing` stuck at true (the nested call
      // parked forever on refreshTokenSubject), wedging every later 401. Failures
      // of the refresh call are handled once, by handle401Error's catchError.
      return next.handle(authReq);
    }

    return next.handle(authReqH).pipe(
      catchError((error: unknown) => {
        if (!(error instanceof HttpErrorResponse)) {
          return throwError(() => error);
        }

        if (error.status === 401) {
          return this.handle401Error(req, next);
        }

        if (error.status === 403) {
          if (this.isSessionInvalid403(error)) {
            // A 403 that genuinely invalidates the session (password change).
            this.expireSession();
          } else {
            // Authorization failure: the user is logged in, they just lack the role.
            // Leave every token in place and simply tell them.
            this.messageService.error(this.permissionDeniedMessage(error));
          }
        }

        return throwError(() => error);
      })
    );
  }

  /** True for the auth endpoints that must bypass the refresh/expiry handling. */
  private isAuthEndpoint(url: string): boolean {
    return AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));
  }

  /** The message the backend put in its jsend envelope, if any. */
  private backendMessage(error: HttpErrorResponse): string {
    const body = error.error;
    return typeof body?.message === 'string' ? body.message : '';
  }

  /** Does this 403 mean the session is dead, rather than "you lack the role"? */
  private isSessionInvalid403(error: HttpErrorResponse): boolean {
    const message = this.backendMessage(error).toLowerCase();
    if (!message) {
      // No message to go on — assume authorization, never log the user out on a guess.
      return false;
    }
    return SESSION_INVALID_403_MARKERS.some((marker) => message.includes(marker));
  }

  private permissionDeniedMessage(error: HttpErrorResponse): string {
    return this.backendMessage(error) || PERMISSION_DENIED_MESSAGE;
  }

  /** Tear the admin session down and send the user back to the login screen. */
  private expireSession(): void {
    this.messageService.error(SESSION_EXPIRED_MESSAGE);
    this.localStorageService.clearAdminTokens();
    this.router.navigate(['auth']);
  }

  /**
   * Fail every request parked on the in-flight refresh instead of leaving them
   * subscribed to a subject that will never emit, then arm a fresh subject.
   */
  private releaseWaitersWithError(err: unknown): void {
    const pending = this.refreshTokenSubject;
    this.refreshTokenSubject = new BehaviorSubject<string | null>(null);
    pending.error(err);
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const deviceToken = this.localStorageService.getDiviceToken();
    let refreshToken: CustomerAccessInput = { refreshToken: '' };
      refreshToken = { refreshToken: this.localStorageService.getRToken() };
    if(this.localStorageService.getRToken()==null){
      this.expireSession();
      return throwError(() => null);
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);
      if (refreshToken) {
        const refreshService = this.injector.get(RefreshAdminService); // Lazy injection here
        return refreshService.refreshAccessToken(refreshToken).pipe(
          // Retry only transient failures. Retrying a rejected refresh token just
          // hammers the endpoint with requests that can never succeed.
          retry({
            count: 3,
            delay: (err) => (this.isRetryableRefreshError(err) ? timer(300) : throwError(() => err))
          }),
          switchMap((res: any) => {
            this.isRefreshing = false;
            const newAccessToken = res.accessToken;
            this.refreshTokenSubject.next(newAccessToken);
            const clonedReq = req.clone({
              headers: req.headers.set('Authorization', `Bearer ${newAccessToken}`)
                .set('Device-Token', deviceToken)
            });
            return next.handle(clonedReq);
          }),
          catchError((err) => {
            this.isRefreshing = false;
            this.releaseWaitersWithError(err);
            // The refresh itself failed — this is the genuine expiry, so surface it
            // rather than silently clearing tokens and leaving a dead screen up.
            this.expireSession();
            return throwError(() => err);
          })
        );
      } else {
        return throwError(() => null);
      }

    } else {
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => {
          const clonedReq = req.clone({
            headers: req.headers.set('Authorization', `Bearer ${token}`)
              .set('Device-Token', deviceToken)
          });
          return next.handle(clonedReq);
        })
      );
    }
  }

  /** A refresh is only worth retrying when the failure looks transient. */
  private isRetryableRefreshError(err: unknown): boolean {
    if (!(err instanceof HttpErrorResponse)) {
      return false;
    }
    return err.status >= 500 || err.status === 0;
  }
  newDiviceToken() {
    const token = this.generateDeviceToken();
    if (!localStorage.getItem('deviceToken')) {
      localStorage.setItem('deviceToken', token);
    }
  }
  generateDeviceToken(length = 20): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let token2 = '';
    for (let i = 0; i < length; i++) {
      token2 += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return token2;
  }
}
